/**
 * ACRES Admin — Analytics overview
 */
import React, { useEffect, useState } from 'react';
import { acresAdmin } from '../../../lib/services/acresService';

export const AcresAnalyticsAdmin: React.FC<{ currentUser?: any }> = () => {
  const [overview, setOverview] = useState<any>(null);
  const [monthly, setMonthly] = useState<any[]>([]);
  const [moderators, setModerators] = useState<any[]>([]);
  const [topUsers, setTopUsers] = useState<any[]>([]);
  const [recent, setRecent] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true); setError(null);
    try {
      const [o, m, mod, tu, r] = await Promise.allSettled([
        acresAdmin.overview(),
        acresAdmin.monthlyRedemption(6),
        acresAdmin.moderatorStats(),
        acresAdmin.topUsers(20),
        acresAdmin.recentSubmissions(15)
      ]);
      if (o.status === 'fulfilled') setOverview(o.value);
      if (m.status === 'fulfilled') setMonthly(m.value?.months || []);
      if (mod.status === 'fulfilled') setModerators(mod.value?.moderators || []);
      if (tu.status === 'fulfilled') setTopUsers(tu.value?.users || []);
      if (r.status === 'fulfilled') setRecent(r.value?.submissions || []);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };

  if (loading) return <div style={{ color: '#9CA3AF' }}>Loading analytics…</div>;
  if (error) return <div style={errBox}>{error}</div>;

  const monthCap = 10000;
  const monthIssued = Number(overview?.shares_issued_this_month || 0);
  const pct = Math.min(100, (monthIssued / monthCap) * 100);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="Total Users (active)" value={overview?.active_users || 0} />
        <Stat label="Total Points Issued" value={Number(overview?.total_points_issued || 0).toLocaleString()} />
        <Stat label="Total Shares Minted" value={Number(overview?.total_shares_minted || 0).toFixed(4)} />
        <Stat label="Submissions (today)" value={overview?.submissions_today || 0} />
        <Stat label="Pending Queue" value={overview?.pending_count || 0} highlight={(overview?.pending_count || 0) > 50} />
        <Stat label="Awaiting 2nd Approval" value={overview?.awaiting_second || 0} />
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontWeight: 600 }}>Monthly Share Cap Usage</div>
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>{monthIssued.toFixed(4)} / {monthCap.toLocaleString()} shares · {pct.toFixed(1)}%</div>
        </div>
        <div style={{ height: 10, background: '#1E1E1E', borderRadius: 5, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: pct > 90 ? '#DC2626' : pct > 70 ? '#F59E0B' : '#16A34A' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12, marginTop: 16 }}>
        <div style={card}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Last 6 Months Redemption</div>
          <table style={tableStyle}>
            <thead><tr style={trHead}><th style={th}>Month</th><th style={th}>Points→Redeemed</th><th style={th}>Shares</th><th style={th}>% of cap</th></tr></thead>
            <tbody>
              {monthly.map(m => (
                <tr key={m.month_year} style={trBody}>
                  <td style={td}>{m.month_year}</td>
                  <td style={td}>{Number(m.points_redeemed || 0).toLocaleString()}</td>
                  <td style={td}>{Number(m.shares_minted || 0).toFixed(4)}</td>
                  <td style={td}>{((Number(m.shares_minted || 0) / monthCap) * 100).toFixed(1)}%</td>
                </tr>
              ))}
              {!monthly.length && <tr><td colSpan={4} style={{ ...td, color: '#9CA3AF', textAlign: 'center' }}>No data</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={card}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Moderator Activity (last 30d)</div>
          <table style={tableStyle}>
            <thead><tr style={trHead}><th style={th}>Moderator</th><th style={th}>Approved</th><th style={th}>Rejected</th><th style={th}>Skipped</th><th style={th}>Avg sec</th></tr></thead>
            <tbody>
              {moderators.map(m => (
                <tr key={m.admin_id} style={trBody}>
                  <td style={td}>{m.admin_name || `#${m.admin_id}`}</td>
                  <td style={td}>{m.approved_count || 0}</td>
                  <td style={td}>{m.rejected_count || 0}</td>
                  <td style={td}>{m.skipped_count || 0}</td>
                  <td style={td}>{m.avg_decision_seconds ? Math.round(m.avg_decision_seconds) : '—'}</td>
                </tr>
              ))}
              {!moderators.length && <tr><td colSpan={5} style={{ ...td, color: '#9CA3AF', textAlign: 'center' }}>No moderator activity</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={card}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Top 20 Earners</div>
          <table style={tableStyle}>
            <thead><tr style={trHead}><th style={th}>#</th><th style={th}>User</th><th style={th}>Points</th><th style={th}>Tasks</th></tr></thead>
            <tbody>
              {topUsers.map((u, i) => (
                <tr key={u.user_id} style={trBody}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}>{u.handle || `#${u.user_id}`}</td>
                  <td style={td}>{Number(u.total_points || 0).toLocaleString()}</td>
                  <td style={td}>{u.tasks_completed || 0}</td>
                </tr>
              ))}
              {!topUsers.length && <tr><td colSpan={4} style={{ ...td, color: '#9CA3AF', textAlign: 'center' }}>No data</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={card}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Recent Submissions</div>
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {recent.map((s, i) => (
              <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid #1E1E1E', fontSize: 12 }}>
                <div>{new Date(s.submitted_at).toLocaleString()} · {s.user_handle || `#${s.user_id}`}</div>
                <div style={{ color: '#9CA3AF' }}>L{s.template_level} · {s.template_title} · {s.status}</div>
              </div>
            ))}
            {!recent.length && <div style={{ color: '#9CA3AF', fontSize: 12 }}>No recent submissions</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: any; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div style={{ ...card, borderColor: highlight ? '#DC2626' : '#1E1E1E' }}>
    <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: highlight ? '#FCA5A5' : '#F59E0B', marginTop: 4 }}>{value}</div>
  </div>
);
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const trHead: React.CSSProperties = { background: '#1E1E1E' };
const trBody: React.CSSProperties = { borderBottom: '1px solid #1E1E1E' };
const th: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', color: '#F59E0B', fontWeight: 600 };
const td: React.CSSProperties = { padding: '6px 8px' };
const card: React.CSSProperties = { background: '#0F0F0F', border: '1px solid #1E1E1E', borderRadius: 8, padding: 14 };
const errBox: React.CSSProperties = { padding: 10, background: 'rgba(220,38,38,0.1)', border: '1px solid #DC2626', borderRadius: 6, color: '#FCA5A5', marginBottom: 10 };
